import { Service } from "typedi";
import { IConfig } from "../../../../../shared/model/config/IConfig";
import { JwtDecoder } from "../../../../../shared/services/jwt-decoder/JwtDecoder";
import { IStJwtPayload } from "../../../../../application/core/models/IStJwtPayload";
import { Locale } from "../../../../../application/core/shared/translator/Locale";
import { ApplePayNetworksService } from "../../models/apple-pay-networks-service/ApplePayNetworksService";
import { IApplePayNamespace } from "../../models/IApplePayNamespace";
import { IEnhancedApplePayPaymentRequest } from "../../models/apple-pay-payment-data/IEnhancedApplePayPaymentRequest";
import { IApplePayValidateMerchantRequest } from "../../models/apple-pay-walletverify-data/IApplePayValidateMerchantRequest";
import { IApplePayConfig } from "../../models/IApplePayConfig";
import { IApplePayConfigObject } from "./IApplePayConfigObject";
import { IApplePaySupportedNetworks } from "../../models/apple-pay-networks-service/IApplePaySupportedNetworks";
import { CurrencyConverter } from "../../../../../shared/services/currency/CurrencyConverter";

@Service()
export class ApplePayConfigService {
  constructor(
    private jwtDecoder: JwtDecoder,
    private applePayNetworkService: ApplePayNetworksService,
    private applePaySessionWrapper: IApplePayNamespace,
    private currencyConverter: CurrencyConverter,
  ) {}

  getConfig(
    config: IConfig,
    validateMerchantRequest: IApplePayValidateMerchantRequest,
  ): IApplePayConfigObject {
    const { applePay, jwt, formId, merchantUrl } = this.getConfigData(config);
    const applePayVersion: number =
      this.applePaySessionWrapper.getLatestSupportedApplePayVersion();
    const { currencyiso3a, mainamount } = this.getStJwtData(jwt);

    return {
      applePayConfig: {
        ...applePay,
        paymentRequest: this.updatePaymentRequest(
          applePay,
          currencyiso3a,
          mainamount,
          applePayVersion,
        ),
      },
      applePayVersion,
      formId,
      jwtFromConfig: jwt,
      merchantUrl,
      validateMerchantRequest: this.updateWalletMerchantId(
        validateMerchantRequest,
        applePay.merchantId,
      ),
      paymentRequest: this.updatePaymentRequest(
        applePay,
        currencyiso3a,
        mainamount,
        applePayVersion,
      ),
    };
  }

  updateWalletValidationUrl(
    validateMerchantRequest: IApplePayValidateMerchantRequest,
    walletvalidationurl: string,
  ): IApplePayValidateMerchantRequest {
    return {
      ...validateMerchantRequest,
      walletvalidationurl,
    };
  }

  private updateCurrencyCode(
    paymentRequest: IEnhancedApplePayPaymentRequest,
    currencyCode: string,
  ): IEnhancedApplePayPaymentRequest {
    return {
      ...paymentRequest,
      currencyCode,
    };
  }

  private updateAmount(
    paymentRequest: IEnhancedApplePayPaymentRequest,
    amount: string,
  ): IEnhancedApplePayPaymentRequest {
    return {
      ...paymentRequest,
      total: {
        ...paymentRequest.total,
        amount,
      },
    };
  }

  private updateRequestTypes(
    paymentRequest: IEnhancedApplePayPaymentRequest,
  ): IEnhancedApplePayPaymentRequest {
    return {
      ...paymentRequest,
    };
  }

  private updateWalletMerchantId(
    validateMerchantRequest: IApplePayValidateMerchantRequest,
    walletmerchantid: string,
  ): IApplePayValidateMerchantRequest {
    return {
      ...validateMerchantRequest,
      walletmerchantid,
    };
  }

  getStJwtData(jwt: string): {
    currencyiso3a: string;
    locale: Locale;
    mainamount: string | undefined;
  } {
    const payload: IStJwtPayload = this.jwtDecoder.decode(jwt).payload;

    if (payload.mainamount != null && payload.mainamount !== "") {
      return {
        currencyiso3a: payload.currencyiso3a,
        locale: payload.locale,
        mainamount: String(payload.mainamount),
      };
    }

    if (
      payload.baseamount != null &&
      payload.baseamount !== "" &&
      payload.currencyiso3a
    ) {
      return {
        currencyiso3a: payload.currencyiso3a,
        locale: payload.locale,
        mainamount: this.currencyConverter.convertFromMinorUnits(
          payload.baseamount,
          payload.currencyiso3a,
        ),
      };
    }

    return {
      currencyiso3a: payload.currencyiso3a,
      locale: payload.locale,
      mainamount: undefined,
    };
  }

  private getConfigData(config: IConfig): {
    applePay: IApplePayConfig;
    formId: string;
    jwt: string;
    merchantUrl: string;
  } {
    return {
      applePay: config.applePay,
      formId: config.formId,
      jwt: config.jwt,
      merchantUrl: config.applePay.merchantUrl,
    };
  }

  private updatePaymentRequest(
    applePay: IApplePayConfig,
    currencyiso3a: string,
    mainamount: string | undefined,
    applePayVersion: number,
  ): IEnhancedApplePayPaymentRequest {
    let paymentRequest: IEnhancedApplePayPaymentRequest =
      applePay.paymentRequest;
    paymentRequest.supportedNetworks =
      this.applePayNetworkService.setSupportedNetworks(
        applePayVersion,
        paymentRequest.supportedNetworks as IApplePaySupportedNetworks[],
      );
    if (mainamount !== undefined) {
      paymentRequest = this.updateAmount(paymentRequest, mainamount);
    }
    paymentRequest = this.updateCurrencyCode(paymentRequest, currencyiso3a);

    return this.updateRequestTypes(paymentRequest);
  }
}
